import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  CircularProgress,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Tooltip,
} from '@mui/material';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import customerService from '../services/customerService';
import routerService from '../services/routerService';
import spopService from '../services/spopService';
import odpService from '../services/odpService';
import packageService from '../services/packageService';
import PPPSecretSelectionDialog from './PPPSecretSelectionDialog';
import CreatePPPSecretDialog from './CreatePPPSecretDialog';

interface AddCustomerDialogProps {
  open: boolean;
  onClose: () => void;
}

interface CustomerFormData {
  customer_code: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  username: string;
  password: string;
  profile: string;
  package_id?: string;
  status: 'active' | 'inactive' | 'suspended' | 'free' | 'NonInternet';
  activation_date?: string;
  router_id: string;
  spop_id?: string;
  odp_id?: string;
}

interface CustomerCreateData {
  customer_code: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  username: string;
  password: string;
  profile: string;
  package_id?: string;
  status: 'active' | 'inactive' | 'suspended' | 'free' | 'NonInternet';
  activation_date?: string;
  router_id: string;
  spop_id?: string;
  odp_id?: string;
  created_at: string;
  updated_at: string;
}

const AddCustomerDialog: React.FC<AddCustomerDialogProps> = ({ open, onClose }) => {
  // Helper function to format bytes
  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const [formData, setFormData] = useState<CustomerFormData>({
    customer_code: '',
    name: '',
    email: '',
    phone: '',
    address: '',
    username: '',
    password: '',
    profile: '',
    package_id: '',
    status: 'active',
    activation_date: '',
    router_id: '',
    spop_id: '',
    odp_id: '',
  });

  const [isGeneratingCode, setIsGeneratingCode] = useState(false);
  const [routerAutoSelected, setRouterAutoSelected] = useState(false);
  const [isCreatingPPP, setIsCreatingPPP] = useState(false);
  const [pppCreationStatus, setPppCreationStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [pppMessage, setPppMessage] = useState('');
  const [showPPPSelectionDialog, setShowPPPSelectionDialog] = useState(false);
  const [showCreatePPPDialog, setShowCreatePPPDialog] = useState(false);
  const [existingPPPSecrets, setExistingPPPSecrets] = useState<any[]>([]);
  const [isLoadingPPPSecrets, setIsLoadingPPPSecrets] = useState(false);
  const [pppSecretsError, setPppSecretsError] = useState('');
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [createdCustomerData, setCreatedCustomerData] = useState<any>(null);

  const queryClient = useQueryClient();

  // Get Routers for dropdown
  const { data: routersData, isLoading: routersLoading } = useQuery({
    queryKey: ['routers-all'],
    queryFn: async () => {
      const response = await routerService.getRouters(1, 1000);
      return response.data.routers;
    },
  });

  // Get SPOPs for dropdown
  const { data: spopsData, isLoading: spopsLoading } = useQuery({
    queryKey: ['spops-all'],
    queryFn: async () => {
      const response = await spopService.getAllSPOPs();
      return response.data.data.spops;
    },
  });

  // Get ODPs for dropdown (filtered by selected SPOP)
  const { data: odpsData, isLoading: odpsLoading, error: odpsError } = useQuery({
    queryKey: ['odps-by-spop', formData.spop_id],
    queryFn: async () => {
      if (!formData.spop_id) return [];
      console.log('Fetching ODPs for SPOP ID:', formData.spop_id);
      const response = await odpService.getODPs(1, 1000, '', undefined, formData.spop_id);
      console.log('Full ODP Response:', response);
      console.log('ODP Data:', response.data);
      console.log('ODPs Array:', response.data.odps);
      return response.data.odps;
    },
    enabled: !!formData.spop_id,
  });

  // Get Packages for dropdown (filtered by selected SPOP)
  const { data: packagesData, isLoading: packagesLoading } = useQuery({
    queryKey: ['packages-by-spop', formData.spop_id],
    queryFn: async () => {
      // Jika SPOP dipilih, filter paket berdasarkan SPOP
      if (formData.spop_id) {
        return await packageService.getAllActivePackages(formData.spop_id);
      }
      
      // Jika SPOP belum dipilih, return semua paket aktif
      return await packageService.getAllActivePackages();
    },
    enabled: true, // Always enabled karena kita butuh data paket
  });

  // Function to generate customer code
  const generateCustomerCode = async () => {
    const selectedSpop = spopsData?.find((spop: any) => spop.id === formData.spop_id);
    const selectedOdp = odpsData?.find((odp: any) => odp.id === formData.odp_id);
    
    if (selectedSpop && selectedOdp) {
      setIsGeneratingCode(true);
      try {
        // Use the new service method to generate unique customer code
        const customerCode = await customerService.generateUniqueCustomerCode(
          selectedSpop.areaCode, 
          selectedOdp.odpCode
        );
        
        console.log('Generated unique customer code:', customerCode);
        
        setFormData(prev => ({
          ...prev,
          customer_code: customerCode
        }));
        
        // Clear any previous PPP error messages when new code is generated
        if (pppCreationStatus === 'error' && pppMessage.includes('Customer code')) {
          setPppCreationStatus('idle');
          setPppMessage('');
        }
        
      } catch (error) {
        console.error('Error generating customer code:', error);
        // Enhanced fallback with more uniqueness
        const timestamp = Date.now().toString();
        const randomSuffix = Math.random().toString(36).substr(2, 3).toUpperCase();
        const fallbackNumber = timestamp.slice(-4) + randomSuffix.slice(0, 2);
        const customerCode = `${selectedSpop.areaCode}${selectedOdp.odpCode}${fallbackNumber}`;
        
        console.log('Fallback customer code:', customerCode);
        
        setFormData(prev => ({
          ...prev,
          customer_code: customerCode
        }));
      } finally {
        setIsGeneratingCode(false);
      }
    }
  };

  // Auto-generate customer code when dependencies change
  React.useEffect(() => {
    if (formData.spop_id && formData.odp_id) {
      generateCustomerCode();
    }
  }, [formData.spop_id, formData.odp_id, spopsData, odpsData]);

  // Clear PPP status when customer code changes and validate uniqueness
  React.useEffect(() => {
    setPppCreationStatus('idle');
    setPppMessage('');
    
    // Validate customer code uniqueness if it exists
    const validateCustomerCode = async () => {
      if (formData.customer_code) {
        try {
          const exists = await customerService.checkCustomerCodeExists(formData.customer_code);
          if (exists) {
            setPppCreationStatus('error');
            setPppMessage(`Customer code "${formData.customer_code}" sudah ada di database. Klik "Generate Ulang" untuk membuat kode baru.`);
          }
        } catch (error) {
          console.error('Error validating customer code:', error);
        }
      }
    };
    
    // Debounce validation to avoid excessive API calls
    const timeoutId = setTimeout(validateCustomerCode, 500);
    return () => clearTimeout(timeoutId);
  }, [formData.customer_code]);

  // Validation for customer code generation
  const canGenerateCode = () => {
    return formData.spop_id && formData.odp_id;
  };

  const getCodeGenerationMessage = () => {
    if (!formData.spop_id) return '⚠️ Pilih SPOP terlebih dahulu';
    if (!formData.odp_id) return '⚠️ Pilih ODP terlebih dahulu';
    if (isGeneratingCode) return '🔄 Sedang generate kode customer...';
    if (formData.customer_code) {
      // Check if there's a conflict message
      if (pppCreationStatus === 'error' && pppMessage.includes('Customer code')) {
        return '❌ Kode customer sudah ada di database';
      }
      return '✅ Kode customer berhasil digenerate';
    }
    return '🔄 Kode customer akan digenerate otomatis';
  };

  // Function to create PPP Secret on MikroTik (Sinkronisasi)
  const createPPPSecret = async () => {
    if (!formData.router_id || !formData.customer_code || !formData.name) {
      setPppCreationStatus('error');
      setPppMessage('Harap lengkapi Router, Kode Customer, dan Nama terlebih dahulu');
      return;
    }

    setIsCreatingPPP(true);
    setPppCreationStatus('idle');
    setPppMessage('');

    try {
      // Check if there are existing PPP secrets for synchronization
      const existingSecretsResponse = await routerService.getPPPSecrets(formData.router_id);
      
      if (existingSecretsResponse.success && existingSecretsResponse.data && existingSecretsResponse.data.length > 0) {
        // Show selection dialog for existing secrets
        setExistingPPPSecrets(existingSecretsResponse.data);
        setShowPPPSelectionDialog(true);
        setIsCreatingPPP(false);
        setPppMessage('Ditemukan PPP Secret yang tersedia untuk sinkronisasi');
        return;
      }

      // If no existing secrets found
      setPppCreationStatus('error');
      setPppMessage('Tidak ada PPP Secret yang ditemukan di router untuk sinkronisasi. Gunakan opsi "Buat PPP Baru" untuk membuat yang baru.');
    } catch (error: any) {
      setPppCreationStatus('error');
      setPppMessage(error.response?.data?.message || 'Error saat mencari PPP Secret untuk sinkronisasi');
    } finally {
      setIsCreatingPPP(false);
    }
  };

  // Function to create new PPP Secret
  const createNewPPPSecret = async (secretData: {
    username: string;
    password: string;
    profile: string;
  }) => {
    if (!formData.router_id || !formData.customer_code || !formData.name) {
      setPppCreationStatus('error');
      setPppMessage('Harap lengkapi Router, Kode Customer, dan Nama terlebih dahulu');
      return;
    }

    setIsCreatingPPP(true);
    setPppCreationStatus('idle');
    setPppMessage('');

    try {
      // Create new PPP Secret with custom data
      const response = await routerService.createPPPSecret(formData.router_id, {
        customerCode: formData.customer_code,
        name: formData.name,
        username: secretData.username,
        password: secretData.password,
        profile: secretData.profile
      });

      if (response.success && response.data) {
        // Auto-fill form with created secret data, but prefer user input for username/password
        setFormData(prev => ({
          ...prev,
          username: secretData.username, // Use user's input
          password: secretData.password, // Use user's input
          profile: response.data!.profile || secretData.profile
        }));
        
        setPppCreationStatus('success');
        setPppMessage(`PPP Secret baru "${secretData.username}" berhasil dibuat di MikroTik dan data telah diisi otomatis`);
      } else {
        setPppCreationStatus('error');
        setPppMessage(response.message || 'Gagal membuat PPP Secret baru');
      }
    } catch (error: any) {
      setPppCreationStatus('error');
      setPppMessage(error.response?.data?.message || 'Error saat membuat PPP Secret baru');
    } finally {
      setIsCreatingPPP(false);
    }
  };

  // Function to handle PPP secret selection from existing ones (Sinkronisasi)
  const handlePPPSecretSelection = async (selectedSecret: any) => {
    setShowPPPSelectionDialog(false);
    setIsLoadingPPPSecrets(true);
    setPppSecretsError('');

    try {
      // Check multiple criteria to ensure no conflicts
      const existingCustomerCheck = await customerService.getCustomers(1, 1000, '');
      const customers = existingCustomerCheck.data?.customers || [];
      
      // Check for username conflict
      const usernameExists = customers.some((customer: any) => customer.username === selectedSecret.name);
      if (usernameExists) {
        setIsLoadingPPPSecrets(false);
        setPppCreationStatus('error');
        setPppMessage(`Username "${selectedSecret.name}" sudah ada di database. Pilih PPP Secret yang lain atau gunakan username yang berbeda.`);
        return;
      }

      // Check for customer code conflict (jika ada)
      if (formData.customer_code) {
        const customerCodeExists = customers.some((customer: any) => customer.customer_code === formData.customer_code);
        if (customerCodeExists) {
          setIsLoadingPPPSecrets(false);
          setPppCreationStatus('error');
          setPppMessage(`Customer code "${formData.customer_code}" sudah ada di database. Generate ulang kode customer atau pilih ODP yang berbeda.`);
          return;
        }
      }

      // If no conflicts, sync from MikroTik
      const syncResult = await customerService.syncPPPSecret(formData.router_id, selectedSecret.name);
      
      if (syncResult) {
        // Auto-fill form with synchronized secret data
        setFormData(prev => ({
          ...prev,
          username: syncResult.username,
          password: syncResult.password,
          profile: syncResult.profile
        }));
        
        setPppCreationStatus('success');
        setPppMessage(`PPP Secret "${selectedSecret.name}" berhasil disinkronisasi dari MikroTik. Data telah diisi otomatis dan siap untuk disimpan ke database.`);
      } else {
        setPppCreationStatus('error');
        setPppMessage('Gagal melakukan sinkronisasi PPP Secret');
      }
    } catch (error: any) {
      setPppCreationStatus('error');
      if (error.response?.data?.message?.includes('sudah ada di database')) {
        setPppMessage(`Username "${selectedSecret.name}" sudah ada di database. Pilih PPP Secret yang lain atau gunakan username yang berbeda.`);
      } else {
        setPppMessage(error.response?.data?.message || 'Error saat melakukan sinkronisasi PPP Secret');
      }
    } finally {
      setIsLoadingPPPSecrets(false);
    }
  };

  // Function to handle creating new PPP secret when user chooses not to use existing ones
  const handleCreateNewPPPSecret = async () => {
    setShowPPPSelectionDialog(false);
    // Redirect to create new PPP Secret dialog
    setShowCreatePPPDialog(true);
  };

  // Check if PPP creation is possible
  const canCreatePPP = () => {
    return formData.router_id && formData.customer_code && formData.name.trim();
  };

  const addCustomerMutation = useMutation({
    mutationFn: (data: CustomerFormData) => {
      // Transform data to match API expectations
      const customerData: CustomerCreateData = {
        ...data,
        // Convert empty strings to undefined for UUID fields
        spop_id: data.spop_id || undefined,
        odp_id: data.odp_id || undefined,
        package_id: data.package_id || undefined,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      return customerService.createCustomer(customerData as any);
    },
    onSuccess: async () => {
      // Customer successfully created (PPP Secret is already created by backend)
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      
      // Prepare data for success dialog
      const selectedPackage = packagesData?.find((pkg: any) => pkg.id === formData.package_id);
      const selectedOdp = odpsData?.find((odp: any) => odp.id === formData.odp_id);
      
      setCreatedCustomerData({
        name: formData.name,
        email: formData.email,
        username: formData.username,
        password: formData.password,
        vlan: selectedOdp?.vlanId || 'N/A',
        package: selectedPackage?.name || 'Tidak ada paket',
        monthlyFee: selectedPackage?.price ? `Rp ${Number(selectedPackage.price).toLocaleString('id-ID')}` : 'Gratis'
      });
      
      // Show success dialog
      setShowSuccessDialog(true);
    },
    onError: (error: any) => {
      console.error('Create customer error:', error);
      
      // Handle specific conflict errors
      if (error.response?.data?.message?.includes('Customer code, email, or username already exists')) {
        // Check which field is causing the conflict and provide specific guidance
        if (pppCreationStatus === 'success') {
          setPppCreationStatus('error');
          setPppMessage('Username dari PPP Secret yang dipilih sudah digunakan oleh customer lain. Pilih PPP Secret yang berbeda atau generate kode customer baru.');
        }
      }
    }
  });

  const resetForm = () => {
    setFormData({
      customer_code: '',
      name: '',
      email: '',
      phone: '',
      address: '',
      username: '',
      password: '',
      profile: '',
      package_id: '',
      status: 'active',
      activation_date: '',
      router_id: '',
      spop_id: '',
      odp_id: '',
    });
    setIsGeneratingCode(false);
    setRouterAutoSelected(false);
    setIsCreatingPPP(false);
    setPppCreationStatus('idle');
    setPppMessage('');
    setShowPPPSelectionDialog(false);
    setShowCreatePPPDialog(false);
    setExistingPPPSecrets([]);
    setIsLoadingPPPSecrets(false);
    setPppSecretsError('');
    setShowSuccessDialog(false);
    setCreatedCustomerData(null);
  };

  const handleInputChange = (field: keyof CustomerFormData) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value,
    }));
    
    // Clear PPP status when relevant fields change
    if (['username', 'password', 'profile'].includes(field)) {
      setPppCreationStatus('idle');
      setPppMessage('');
    }
  };

  const handleSelectChange = (field: keyof CustomerFormData) => (
    e: any
  ) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value,
      // Reset ODP and customer code when SPOP changes
      ...(field === 'spop_id' && { odp_id: '', customer_code: '' }),
      // Reset customer code when ODP changes
      ...(field === 'odp_id' && { customer_code: '' }),
      // Auto-fill profile when package changes
      ...(field === 'package_id' && value && {
        profile: packagesData?.find((pkg: any) => pkg.id === value)?.profile_name || ''
      }),
    }));

    // Clear PPP status when router changes
    if (field === 'router_id') {
      setPppCreationStatus('idle');
      setPppMessage('');
    }

    // Auto-select router when SPOP is selected
    if (field === 'spop_id' && value && routersData) {
      const associatedRouter = routersData.find((router: any) => router.spopdId === value);
      if (associatedRouter) {
        setFormData(prev => ({
          ...prev,
          router_id: String(associatedRouter.id),
          [field]: value,
          odp_id: '',
          customer_code: ''
        }));
        setRouterAutoSelected(true);
        // Reset auto-selection notification after 3 seconds
        setTimeout(() => setRouterAutoSelected(false), 3000);
      } else {
        // Reset router if no associated router found
        setFormData(prev => ({
          ...prev,
          router_id: '',
          [field]: value,
          odp_id: '',
          customer_code: ''
        }));
      }
      
      // Clear PPP status when SPOP changes
      setPppCreationStatus('idle');
      setPppMessage('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.username || !formData.password || !formData.router_id || !formData.customer_code) {
      return;
    }

    // Pre-validation: Check for conflicts before submitting
    try {
      const existingCustomersCheck = await customerService.getCustomers(1, 1000, '');
      const customers = existingCustomersCheck.data?.customers || [];
      
      // Check for username conflict
      const usernameConflict = customers.find((customer: any) => customer.username === formData.username);
      if (usernameConflict) {
        setPppCreationStatus('error');
        setPppMessage(`Username "${formData.username}" sudah digunakan oleh customer "${usernameConflict.name}" (${usernameConflict.customer_code}). Pilih PPP Secret yang berbeda atau gunakan username lain.`);
        return;
      }

      // Check for customer code conflict
      const customerCodeConflict = customers.find((customer: any) => customer.customer_code === formData.customer_code);
      if (customerCodeConflict) {
        setPppCreationStatus('error');
        setPppMessage(`Customer code "${formData.customer_code}" sudah digunakan oleh customer "${customerCodeConflict.name}". Generate ulang kode customer.`);
        return;
      }

      // Check for email conflict
      const emailConflict = customers.find((customer: any) => customer.email.toLowerCase() === formData.email.toLowerCase());
      if (emailConflict) {
        setPppCreationStatus('error');
        setPppMessage(`Email "${formData.email}" sudah digunakan oleh customer "${emailConflict.name}" (${emailConflict.customer_code}). Gunakan email yang berbeda.`);
        return;
      }

    } catch (error) {
      console.error('Pre-validation error:', error);
      // Continue with submission if validation check fails
    }

    // Ensure profile is set from selected package if available
    let finalFormData = { ...formData };
    if (formData.package_id && !formData.profile) {
      const selectedPackage = packagesData?.find((pkg: any) => pkg.id === formData.package_id);
      if (selectedPackage?.profile_name) {
        finalFormData.profile = selectedPackage.profile_name;
        // Update form state for immediate UI feedback
        setFormData(prev => ({ ...prev, profile: selectedPackage.profile_name }));
      }
    }

    console.log('Submitting customer with auto PPP creation:', {
      customer: finalFormData.customer_code,
      username: finalFormData.username,
      password: finalFormData.password,
      profile: finalFormData.profile,
      router: finalFormData.router_id
    });

    addCustomerMutation.mutate(finalFormData);
  };

  const handleClose = () => {
    if (!addCustomerMutation.isPending) {
      resetForm();
      onClose();
    }
  };

  const handleSuccessDialogClose = () => {
    setShowSuccessDialog(false);
    setCreatedCustomerData(null);
    onClose();
    resetForm();
  };

  // Function to handle manual PPP secret input (untuk auto-fill form tanpa membuat PPP)
  const handleCreatePPPSecretSubmit = async (secretData: {
    username: string;
    password: string;
    profile: string;
  }) => {
    setShowCreatePPPDialog(false);
    
    // Hanya auto-fill form tanpa membuat PPP Secret di router
    setFormData(prev => ({
      ...prev,
      username: secretData.username,
      password: secretData.password,
      profile: secretData.profile
    }));
    
    setPppCreationStatus('success');
    setPppMessage('Form berhasil diisi dengan data PPP. PPP Secret akan dibuat di MikroTik saat menyimpan customer.');
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>Tambah Customer Baru</DialogTitle>
        
        <DialogContent>
            
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <Box display="flex" gap={2}>
              <FormControl fullWidth>
                <InputLabel>SPOP</InputLabel>
                <Select
                  value={formData.spop_id || ''}
                  onChange={handleSelectChange('spop_id')}
                  label="SPOP"
                  disabled={spopsLoading}
                >
                  <MenuItem value="">
                    <em>Pilih SPOP</em>
                  </MenuItem>
                  {spopsData?.map((spop: any) => (
                    <MenuItem key={spop.id} value={spop.id}>
                      {spop.name} - {spop.areaCode}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <FormControl fullWidth>
                <InputLabel>ODP</InputLabel>
                <Select
                  value={formData.odp_id || ''}
                  onChange={handleSelectChange('odp_id')}
                  label="ODP"
                  disabled={odpsLoading || !formData.spop_id}
                >
                  <MenuItem value="">
                    <em>Pilih ODP</em>
                  </MenuItem>
                  {odpsData?.map((odp: any) => {
                    console.log('Rendering ODP:', odp);
                    return (
                      <MenuItem key={odp.id} value={odp.id}>
                        {odp.fullCode}
                      </MenuItem>
                    );
                  })}
                </Select>
                {odpsError && (
                  <Box mt={1}>
                    <Typography variant="caption" color="error">
                      Error loading ODPs: {(odpsError as any).message}
                    </Typography>
                  </Box>
                )}
                {odpsLoading && (
                  <Box mt={1}>
                    <Typography variant="caption">
                      Loading ODPs...
                    </Typography>
                  </Box>
                )}
                {formData.spop_id && !odpsLoading && odpsData?.length === 0 && (
                  <Box mt={1}>
                    <Typography variant="caption" color="warning">
                      No ODPs found for selected SPOP
                    </Typography>
                  </Box>
                )}
              </FormControl>
            </Box>
            
              <TextField
                label="Nama Lengkap"
                value={formData.name}
                onChange={handleInputChange('name')}
                required
                fullWidth
                placeholder="John Doe"
              />
              <Box sx={{ flex: 1 }} width={"50%"}>
                <TextField
                  label="Kode Customer"
                  value={formData.customer_code}
                  fullWidth
                  placeholder="Akan digenerate otomatis"
                  InputProps={{
                    readOnly: true,
                  }}
                  disabled
                  helperText={getCodeGenerationMessage()}
                  color={
                    pppCreationStatus === 'error' && pppMessage.includes('Customer code') 
                      ? "error" 
                      : formData.customer_code 
                      ? "success" 
                      : "primary"
                  }
                />
                <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
                  {formData.customer_code && (
                    <Chip 
                      label={
                        pppCreationStatus === 'error' && pppMessage.includes('Customer code')
                          ? "Kode Konflik ⚠️"
                          : "Kode Generated ✓"
                      } 
                      color={
                        pppCreationStatus === 'error' && pppMessage.includes('Customer code')
                          ? "error"
                          : "success"
                      } 
                      size="small" 
                      variant="outlined"
                    />
                  )}
                  {canGenerateCode() && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={generateCustomerCode}
                      disabled={isGeneratingCode}
                      sx={{ fontSize: '0.75rem' }}
                      startIcon={isGeneratingCode ? <CircularProgress size={12} /> : null}
                      color={
                        pppCreationStatus === 'error' && pppMessage.includes('Customer code')
                          ? "error"
                          : "primary"
                      }
                    >
                      {isGeneratingCode ? 'Generating...' : '🔄 Generate Ulang'}
                    </Button>
                  )}
                </Box>
                {canGenerateCode() && !formData.customer_code && (
                  <Box sx={{ mt: 1, p: 1, bgcolor: 'info.light', borderRadius: 1 }}>
                    <Typography variant="caption" color="info.dark">
                      Preview: {spopsData?.find((s: any) => s.id === formData.spop_id)?.areaCode}{odpsData?.find((o: any) => o.id === formData.odp_id)?.odpCode}XXX (akan digenerate nomor unik)
                    </Typography>
                  </Box>
                )}
             
            </Box>
            
            <Box display="flex" gap={2}>
              <TextField
                label="Email"
                type="email"
                value={formData.email}
                onChange={handleInputChange('email')}
                required
                fullWidth
                placeholder="john@example.com"
              />
              <TextField
                label="No. Telepon"
                value={formData.phone}
                onChange={handleInputChange('phone')}
                fullWidth
                placeholder="081234567890"
              />
            </Box>
              
            <TextField
              label="Alamat"
              value={formData.address}
              onChange={handleInputChange('address')}
              fullWidth
              multiline
              rows={2}
              placeholder="Alamat lengkap customer"
            />

            <FormControl fullWidth required>
              <InputLabel>Router</InputLabel>
              <Select
                value={formData.router_id}
                onChange={handleSelectChange('router_id')}
                label="Router"
                disabled={routersLoading || !formData.spop_id}
              >
                <MenuItem value="">
                  <em>Pilih Router</em>
                </MenuItem>
                {routersData
                  ?.filter((router: any) => 
                    !formData.spop_id || router.spopdId === formData.spop_id
                  )
                  .map((router: any) => (
                    <MenuItem key={router.id} value={String(router.id)}>
                      {router.name} - {router.ip_address}
                      {router.spopdId === formData.spop_id && (
                        <Chip 
                          label="SPOP Match" 
                          size="small" 
                          color="success" 
                          sx={{ ml: 1, fontSize: '0.7rem' }}
                        />
                      )}
                    </MenuItem>
                  ))}
              </Select>
              {formData.spop_id && routersData?.filter((router: any) => router.spopdId === formData.spop_id).length === 0 && (
                <Typography variant="caption" color="warning" sx={{ mt: 1 }}>
                  ⚠️ Tidak ada router yang terkait dengan SPOP ini
                </Typography>
              )}
              {routerAutoSelected && (
                <Box sx={{ mt: 1, p: 1, bgcolor: 'success.light', borderRadius: 1 }}>
                  <Typography variant="caption" color="success.dark">
                    ✅ Router otomatis dipilih berdasarkan SPOP yang dipilih
                  </Typography>
                </Box>
              )}
            </FormControl>

            {/* PPP Secret Creation Section */}
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.300' }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                🔐 Kelola PPP Secret MikroTik
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Pilih metode untuk mengelola PPP Secret pada router MikroTik
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {/* Option 1: Sinkronisasi PPP Secret */}
                <Box sx={{ p: 2, border: '1px dashed', borderColor: 'primary.main', borderRadius: 1, bgcolor: 'rgba(33, 150, 243, 0.05)' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'primary.main', mb: 1, display: 'block' }}>
                    🔄 Opsi 1: Sinkronisasi PPP Secret
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                    Pilih PPP Secret yang sudah ada di router MikroTik. Jika username sudah ada di MikroTik tapi belum ada di database, sistem akan otomatis menyinkronkan data dan menyimpannya ke database.
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={createPPPSecret}
                    disabled={!canCreatePPP() || isCreatingPPP}
                    startIcon={isCreatingPPP ? <CircularProgress size={16} /> : null}
                    sx={{ 
                      minWidth: '180px',
                      background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                      '&:hover': {
                        background: 'linear-gradient(45deg, #1976D2 30%, #1CB5E0 90%)',
                      }
                    }}
                  >
                    {isCreatingPPP ? 'Mencari...' : '� Sinkronisasi PPP'}
                  </Button>
                </Box>

                {/* Option 2: Buat PPP Secret Baru */}
                <Box sx={{ p: 2, border: '1px dashed', borderColor: 'success.main', borderRadius: 1, bgcolor: 'rgba(76, 175, 80, 0.05)' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'success.main', mb: 1, display: 'block' }}>
                    ✨ Opsi 2: Buat PPP Secret Baru
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                    Auto-fill form dengan kode customer. PPP Secret akan dibuat saat menyimpan customer.
                  </Typography>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={() => {
                      if (formData.customer_code) {
                        // Hanya auto-fill form tanpa membuat PPP Secret di router
                        setFormData(prev => ({
                          ...prev,
                          username: formData.customer_code,
                          password: formData.customer_code,
                          profile: formData.profile || (formData.package_id && packagesData?.find((pkg: any) => pkg.id === formData.package_id)?.profile_name) || 'default'
                        }));
                        
                        setPppCreationStatus('success');
                        setPppMessage('Form berhasil diisi dengan kode customer. PPP Secret akan dibuat di MikroTik saat menyimpan customer.');
                      } else {
                        // Jika belum ada kode customer, buka dialog
                        setShowCreatePPPDialog(true);
                      }
                    }}
                    disabled={!formData.customer_code}
                    sx={{ 
                      minWidth: '180px',
                      background: 'linear-gradient(45deg, #4CAF50 30%, #66BB6A 90%)',
                      '&:hover': {
                        background: 'linear-gradient(45deg, #388E3C 30%, #4CAF50 90%)',
                      }
                    }}
                  >
                    ✨ Buat PPP Baru
                  </Button>
                  {formData.customer_code && (
                    <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'success.main' }}>
                      Username & Password: {formData.customer_code}
                    </Typography>
                  )}
                  {!formData.customer_code && (
                    <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'warning.main' }}>
                      Generate kode customer terlebih dahulu
                    </Typography>
                  )}
                </Box>
                
                {pppCreationStatus !== 'idle' && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                    <Chip
                      label={pppCreationStatus === 'success' ? 'Berhasil ✓' : 'Error ✗'}
                      color={pppCreationStatus === 'success' ? 'success' : 'error'}
                      size="medium"
                      variant="filled"
                      sx={{ fontWeight: 'bold' }}
                    />
                  </Box>
                )}
              </Box>

              {pppMessage && (
                <Alert 
                  severity={pppCreationStatus === 'success' ? 'success' : 'error'} 
                  sx={{ mt: 2 }}
                  variant="filled"
                  action={
                    pppCreationStatus === 'error' && (
                      pppMessage.includes('Customer code') || 
                      pppMessage.includes('Username') || 
                      pppMessage.includes('Email')
                    ) ? (
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {pppMessage.includes('Customer code') && (
                          <Button 
                            color="inherit" 
                            size="small" 
                            onClick={generateCustomerCode}
                            disabled={isGeneratingCode}
                          >
                            Generate Ulang Kode
                          </Button>
                        )}
                        {pppMessage.includes('Username') && (
                          <Button 
                            color="inherit" 
                            size="small" 
                            onClick={() => {
                              setPppCreationStatus('idle');
                              setPppMessage('');
                              setFormData(prev => ({ ...prev, username: '', password: '', profile: '' }));
                            }}
                          >
                            Reset PPP
                          </Button>
                        )}
                      </Box>
                    ) : undefined
                  }
                >
                  {pppMessage}
                </Alert>
              )}

              {!canCreatePPP() && (
                <Box sx={{ mt: 2, p: 1, bgcolor: 'warning.light', borderRadius: 1 }}>
                  <Typography variant="caption" color="warning.dark">
                    ⚠️ Harap lengkapi Router, Kode Customer, dan Nama terlebih dahulu untuk Sinkronisasi PPP
                  </Typography>
                </Box>
              )}
              
              {!formData.router_id && (
                <Box sx={{ mt: 2, p: 1, bgcolor: 'info.light', borderRadius: 1 }}>
                  <Typography variant="caption" color="info.dark">
                    ℹ️ Pilih Router terlebih dahulu untuk menggunakan fitur PPP Secret
                  </Typography>
                </Box>
              )}
            </Box>
              
            <Box display="flex" gap={2}>
              <Box sx={{ flex: 1 }}>
                <TextField
                  label="Username"
                  value={formData.username}
                  onChange={handleInputChange('username')}
                  required
                  fullWidth
                  placeholder="john_doe"
                  color={pppCreationStatus === 'success' ? 'success' : 'primary'}
                />
                {pppCreationStatus === 'success' && formData.username && (
                  <Typography variant="caption" color="success.main" sx={{ mt: 0.5 }}>
                    ✅ Auto-filled dari PPP Secret
                  </Typography>
                )}
              </Box>
              <Box sx={{ flex: 1 }}>
                <TextField
                  label="Password"
                  type="password"
                  value={formData.password}
                  onChange={handleInputChange('password')}
                  required
                  fullWidth
                  placeholder="Password"
                  color={pppCreationStatus === 'success' ? 'success' : 'primary'}
                />
                {pppCreationStatus === 'success' && formData.password && (
                  <Typography variant="caption" color="success.main" sx={{ mt: 0.5 }}>
                    ✅ Auto-filled dari PPP Secret
                  </Typography>
                )}
              </Box>
            </Box>
              
            <Box display="flex" gap={2}>
              <Box sx={{ flex: 1 }}>
                <TextField
                  label="Profile"
                  value={formData.profile}
                  onChange={handleInputChange('profile')}
                  fullWidth
                  placeholder="Profile paket"
                  color={pppCreationStatus === 'success' ? 'success' : formData.package_id && formData.profile ? 'info' : 'primary'}
                />
                {pppCreationStatus === 'success' && formData.username && (
                  <Typography variant="caption" color="success.main" sx={{ mt: 0.5 }}>
                    ✅ Auto-filled dari PPP Secret
                  </Typography>
                )}
                {formData.package_id && formData.profile && pppCreationStatus !== 'success' && (
                  <Typography variant="caption" color="info.main" sx={{ mt: 0.5 }}>
                    📦 Auto-filled dari paket yang dipilih
                  </Typography>
                )}
              </Box>
              <FormControl sx={{ width: "50%" }}>
                <InputLabel>Paket Internet</InputLabel>
                <Select
                  value={formData.package_id || ''}
                  onChange={handleSelectChange('package_id')}
                  label="Paket Internet"
                  disabled={packagesLoading}
                >
                  <MenuItem value="">
                    <em>Pilih Paket Internet</em>
                  </MenuItem>
                  {packagesData?.map((pkg: any) => (
                    <MenuItem key={pkg.id} value={pkg.id}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {pkg.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {/* Display bandwidth info */}
                          {pkg.bandwidth_upload && pkg.bandwidth_download 
                            ? `↑${pkg.bandwidth_upload} Mbps / ↓${pkg.bandwidth_download} Mbps` 
                            : pkg.bandwidth_download 
                            ? `${pkg.bandwidth_download} Mbps` 
                            : 'Unlimited'
                          }
                          {/* Display price */}
                          {pkg.price !== null && pkg.price !== undefined 
                            ? ` • Rp ${Number(pkg.price).toLocaleString('id-ID')}` 
                            : ' • Free'
                          }
                          {/* Display quota if exists */}
                          {pkg.quota_bytes && ` • Quota: ${formatBytes(pkg.quota_bytes)}`}
                        </Typography>
                        {/* Display profile name */}
                        {pkg.profile_name && (
                          <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 'medium' }}>
                            Profile: {pkg.profile_name}
                          </Typography>
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {packagesLoading && (
                  <Typography variant="caption" sx={{ mt: 0.5 }}>
                    🔄 Loading paket internet...
                  </Typography>
                )}
                {!packagesLoading && packagesData?.length === 0 && (
                  <Typography variant="caption" color="warning.main" sx={{ mt: 0.5 }}>
                    ⚠️ Tidak ada paket internet aktif tersedia
                  </Typography>
                )}
              </FormControl>
            </Box>
              
            <Box display="flex" gap={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  onChange={handleSelectChange('status')}
                  label="Status"
                >
                  <MenuItem value="active">Aktif</MenuItem>
                  <MenuItem value="inactive">Non-aktif</MenuItem>
                  <MenuItem value="suspended">Ditangguhkan</MenuItem>
                  <MenuItem value="free">Free</MenuItem>
                  <MenuItem value="NonInternet">Non Internet</MenuItem>
                </Select>
              </FormControl>
              
              <TextField
                label="Tanggal Aktivasi"
                type="date"
                value={formData.activation_date}
                onChange={handleInputChange('activation_date')}
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
                helperText="Pilih tanggal aktivasi customer"
              />
            </Box>

            {addCustomerMutation.error && (
              <Alert 
                severity="error"
                action={
                  (addCustomerMutation.error as any).response?.data?.message?.includes('already exists') ? (
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button 
                        color="inherit" 
                        size="small" 
                        onClick={generateCustomerCode}
                        disabled={isGeneratingCode}
                      >
                        Generate Ulang Kode
                      </Button>
                      <Button 
                        color="inherit" 
                        size="small" 
                        onClick={() => {
                          setPppCreationStatus('idle');
                          setPppMessage('');
                          setFormData(prev => ({ ...prev, username: '', password: '', profile: '' }));
                        }}
                      >
                        Reset PPP
                      </Button>
                    </Box>
                  ) : undefined
                }
              >
                {(addCustomerMutation.error as any).response?.data?.message === 'Customer code, email, or username already exists' 
                  ? 'Data customer sudah ada di database. Periksa kode customer, email, atau username yang digunakan.' 
                  : (addCustomerMutation.error as any).response?.data?.message || 'Error saat menambah customer'
                }
              </Alert>
            )}

            {/* Auto PPP Creation Info */}
            {formData.router_id && formData.customer_code && formData.username && formData.password && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  🔄 PPP Secret akan dibuat otomatis setelah customer disimpan
                </Typography>
                <Typography variant="caption" sx={{ display: 'block' }}>
                  • Username: {formData.username}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block' }}>
                  • Password: {formData.password}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block' }}>
                  • Profile: {formData.profile || (formData.package_id && packagesData?.find((pkg: any) => pkg.id === formData.package_id)?.profile_name) || 'default'}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block' }}>
                  • Router: {routersData?.find((r: any) => String(r.id) === formData.router_id)?.name}
                </Typography>
              </Alert>
            )}
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button 
            onClick={handleClose}
            disabled={addCustomerMutation.isPending}
          >
            Batal
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={
              addCustomerMutation.isPending ||
              !formData.name ||
              !formData.email ||
              !formData.username ||
              !formData.password ||
              !formData.router_id ||
              !formData.customer_code
            }
            startIcon={addCustomerMutation.isPending ? <CircularProgress size={16} /> : null}
          >
            {addCustomerMutation.isPending ? 'Menyimpan...' : 'Simpan'}
          </Button>
        </DialogActions>
      </form>

      {/* PPP Secret Selection Dialog */}
      <PPPSecretSelectionDialog
        open={showPPPSelectionDialog}
        onClose={() => setShowPPPSelectionDialog(false)}
        onSelect={handlePPPSecretSelection}
        onCreateNew={handleCreateNewPPPSecret}
        secrets={existingPPPSecrets}
        isLoading={isLoadingPPPSecrets}
        error={pppSecretsError}
      />

      {/* Create PPP Secret Dialog */}
      <CreatePPPSecretDialog
        open={showCreatePPPDialog}
        onClose={() => setShowCreatePPPDialog(false)}
        onCreateSecret={handleCreatePPPSecretSubmit}
        routerId={formData.router_id}
        routerName={routersData?.find((r: any) => String(r.id) === formData.router_id)?.name}
        isLoading={isCreatingPPP}
      />

      {/* Success Dialog */}
      <Dialog
        open={showSuccessDialog}
        onClose={handleSuccessDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'success.main', color: 'white', textAlign: 'center' }}>
          ✅ Customer Berhasil Dibuat
        </DialogTitle>
        
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity="success" variant="filled">
              Customer dan PPP Secret berhasil dibuat di database dan MikroTik router
            </Alert>

            {createdCustomerData && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Nama Customer:</Typography>
                  <Typography variant="body2">{createdCustomerData.name}</Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Email:</Typography>
                  <Typography variant="body2">{createdCustomerData.email}</Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, bgcolor: 'primary.light', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.dark' }}>Username:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.dark' }}>{createdCustomerData.username}</Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, bgcolor: 'primary.light', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.dark' }}>Password:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.dark' }}>{createdCustomerData.password}</Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, bgcolor: 'warning.light', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'warning.dark' }}>VLAN:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'warning.dark' }}>{createdCustomerData.vlan}</Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, bgcolor: 'info.light', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'info.dark' }}>Paket Internet:</Typography>
                  <Typography variant="body2" sx={{ color: 'info.dark' }}>{createdCustomerData.package}</Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, bgcolor: 'success.light', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'success.dark' }}>Biaya Bulanan:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'success.dark' }}>{createdCustomerData.monthlyFee}</Typography>
                </Box>
              </Box>
            )}

            <Alert severity="info" variant="outlined">
              <Typography variant="caption">
                Data customer telah tersimpan dan PPP Secret telah dibuat di router MikroTik. 
                Customer dapat langsung menggunakan koneksi dengan username dan password di atas.
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleSuccessDialogClose}
            variant="contained"
            color="success"
            fullWidth
            sx={{ py: 1.5 }}
          >
            Tutup
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default AddCustomerDialog;
